import * as React from 'react';
import type {ReactNode} from 'react';
import {AnimatePresence, motion, useReducedMotion} from 'motion/react';
import type {ChatToolCall} from '../hooks/chat/types';
import type {IconComponent} from '../icons';
import {Icons} from '../icons';
import {cn} from '../lib/utils';
import {useChatSurfaceVariant, type ChatSurfaceVariant} from './chat-variant';
import {ThinkingLabel} from './thinking';

export interface ToolCallConfig {
  icon?: IconComponent;
  /**
   * Phrase it in progress ("Searching"); the finished trail derives the past
   * tense from its leading verb ("Searched").
   */
  label?: string;
  /**
   * How the collapsed trail counts this tool: `{one: 'search', many: 'searches'}`
   * becomes "6 searches". Tools without one are counted as plain steps.
   */
  summary?: {one: string; many: string};
}

export type ToolCallConfigMap = Record<string, ToolCallConfig>;

const TOOL_CALL_ROW = 'flex items-start gap-2 text-sm text-muted-foreground';

export interface ToolCallStackProps {
  toolCalls: ChatToolCall[];
  toolConfig?: ToolCallConfigMap;
  className?: string;
}

/**
 * Last-resort label for a tool nothing has configured — `get_meeting` reads as
 * "Get meeting" rather than leaking the wire name into the transcript. New
 * runtime tools land here until someone gives them a written label.
 */
function humanizeToolName(name: string) {
  const words = name.replace(/[_-]+/g, ' ').trim();

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}

/**
 * The leading verbs the tools and the model use in progress, with the form the
 * finished trail shows. A verb missing here is left as written, so the worst
 * case is a step that still reads as in progress, never a wrong word.
 */
const PAST_TENSE: Record<string, string> = {
  Adding: 'Added',
  Analyzing: 'Analyzed',
  Building: 'Built',
  Calculating: 'Calculated',
  Checking: 'Checked',
  Collecting: 'Collected',
  Comparing: 'Compared',
  Compiling: 'Compiled',
  Confirming: 'Confirmed',
  Counting: 'Counted',
  Creating: 'Created',
  Drafting: 'Drafted',
  Extracting: 'Extracted',
  Fetching: 'Fetched',
  Filtering: 'Filtered',
  Finding: 'Found',
  Gathering: 'Gathered',
  Generating: 'Generated',
  Identifying: 'Identified',
  Listing: 'Listed',
  Loading: 'Loaded',
  Locating: 'Located',
  Looking: 'Looked',
  Opening: 'Opened',
  Preparing: 'Prepared',
  Pulling: 'Pulled',
  Querying: 'Queried',
  Reading: 'Read',
  Resolving: 'Resolved',
  Retrieving: 'Retrieved',
  Reviewing: 'Reviewed',
  Saving: 'Saved',
  Scheduling: 'Scheduled',
  Searching: 'Searched',
  Sending: 'Sent',
  Starting: 'Started',
  Summarizing: 'Summarized',
  Updating: 'Updated',
  Verifying: 'Verified',
  Writing: 'Wrote',
};

function stripEllipsis(label: string) {
  return label.replace(/(\.{3}|…|\.)\s*$/, '');
}

/** "Searching notes" becomes "Searched notes" once the tool has finished. */
export function finishLabel(label: string) {
  return stripEllipsis(label).replace(
    /^\w+/,
    (verb) => PAST_TENSE[verb] ?? verb,
  );
}

function baseLabel(
  toolCall: ChatToolCall,
  toolConfig: ToolCallConfigMap | undefined,
) {
  return (
    toolCall.intent ??
    toolConfig?.[toolCall.name]?.label ??
    humanizeToolName(toolCall.name)
  );
}

/** The in-progress label for the tool the agent is running now. */
export function toolCallLabel(
  toolCall: ChatToolCall,
  toolConfig: ToolCallConfigMap | undefined,
  variant: ChatSurfaceVariant,
) {
  if (variant !== 'agent') {
    return (
      toolCall.intent ?? toolConfig?.[toolCall.name]?.label ?? toolCall.name
    );
  }

  return `${stripEllipsis(baseLabel(toolCall, toolConfig))}...`;
}

function getToolCallDisplay(
  toolCall: ChatToolCall,
  toolConfig: ToolCallConfigMap | undefined,
  variant: ChatSurfaceVariant,
) {
  const config = toolConfig?.[toolCall.name];
  const isDone = !toolCall.streaming;
  const Icon = isDone ? (config?.icon ?? Icons.check) : Icons.spinner;

  if (variant !== 'agent') {
    return {
      Icon,
      isDone,
      label: toolCall.intent ?? config?.label ?? toolCall.name,
    };
  }

  const label = isDone
    ? finishLabel(baseLabel(toolCall, toolConfig))
    : toolCallLabel(toolCall, toolConfig, variant);

  return {Icon, isDone, label};
}

interface TrailStep {
  key: string;
  Icon: IconComponent;
  label: string;
  count: number;
}

/** Finished calls in order, consecutive repeats folded together. */
function trailSteps(
  toolCalls: ChatToolCall[],
  toolConfig: ToolCallConfigMap | undefined,
  variant: ChatSurfaceVariant,
): TrailStep[] {
  const steps: TrailStep[] = [];

  toolCalls.forEach((toolCall) => {
    if (toolCall.streaming) {
      return;
    }

    const {label} = getToolCallDisplay(toolCall, toolConfig, variant);
    const Icon = toolConfig?.[toolCall.name]?.icon ?? Icons.check;
    const previous = steps[steps.length - 1];
    if (previous && previous.label === label) {
      previous.count += 1;
      return;
    }
    steps.push({key: toolCall.toolCallId, Icon, label, count: 1});
  });

  return steps;
}

/**
 * "14 steps · 6 searches · 8 records read": a count of the work rather than
 * its sentences, so the collapsed line never clips and says what kind of work
 * it was before it is opened.
 */
export function summarizeToolCalls(
  toolCalls: ChatToolCall[],
  toolConfig?: ToolCallConfigMap,
): string {
  const finished = toolCalls.filter((toolCall) => !toolCall.streaming);
  const kinds = new Map<string, {one: string; many: string; count: number}>();

  finished.forEach((toolCall) => {
    const summary = toolConfig?.[toolCall.name]?.summary;
    if (!summary) {
      return;
    }
    const entry = kinds.get(summary.many) ?? {...summary, count: 0};
    entry.count += 1;
    kinds.set(summary.many, entry);
  });

  const parts = [
    `${finished.length} ${finished.length === 1 ? 'step' : 'steps'}`,
    ...Array.from(
      kinds.values(),
      ({one, many, count}) => `${count} ${count === 1 ? one : many}`,
    ),
  ];

  return parts.join(' · ');
}

const STEP_TRANSITION = {duration: 0.2, ease: [0.2, 0, 0, 1]} as const;
const NO_TRANSITION = {duration: 0} as const;

const FOLD = {
  initial: {opacity: 0, height: 0},
  animate: {opacity: 1, height: 'auto'},
  exit: {opacity: 0, height: 0},
} as const;

function useStepTransition() {
  return useReducedMotion() ? NO_TRANSITION : STEP_TRANSITION;
}

function TrailRow({children}: {children: ReactNode}) {
  const transition = useStepTransition();

  return (
    <motion.li {...FOLD} transition={transition} className="overflow-hidden">
      <div className="flex items-start gap-2 py-1">{children}</div>
    </motion.li>
  );
}

export interface ToolCallTrailListProps extends ToolCallStackProps {
  /** What the agent is doing now, as the last row. */
  pendingLabel?: string;
}

/** The steps behind a response, each with its tool's icon, repeats folded. */
export const ToolCallTrailList = ({
  toolCalls,
  toolConfig,
  pendingLabel,
  className,
}: ToolCallTrailListProps) => {
  const variant = useChatSurfaceVariant();
  const steps = trailSteps(toolCalls, toolConfig, variant);
  if (steps.length === 0 && !pendingLabel) {
    return null;
  }

  return (
    <ol
      className={cn(
        'ml-[7px] grid overflow-hidden border-l border-border/70 pl-4 text-xs text-muted-foreground',
        className,
      )}
    >
      <AnimatePresence initial={false}>
        {steps.map((step) => (
          <TrailRow key={step.key}>
            <step.Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/70" />
            <span className="min-w-0 leading-5">
              {step.label}
              {step.count > 1 && (
                <span className="ml-1.5 text-muted-foreground/60">
                  ×{step.count}
                </span>
              )}
            </span>
          </TrailRow>
        ))}
        {pendingLabel && (
          <TrailRow key="pending">
            <Icons.dotFilled className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/70" />
            <ThinkingLabel className="min-w-0 leading-5">
              {pendingLabel}
            </ThinkingLabel>
          </TrailRow>
        )}
      </AnimatePresence>
    </ol>
  );
};

/** The agent's live work as one list that unfolds when the turn starts and folds away when it ends. */
export const ToolCallActivity = (props: ToolCallTrailListProps) => {
  const transition = useStepTransition();
  const visible =
    Boolean(props.pendingLabel) ||
    props.toolCalls.some((toolCall) => !toolCall.streaming);

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div
          key="activity"
          {...FOLD}
          transition={transition}
          className="overflow-hidden"
        >
          <ToolCallTrailList {...props} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const ToolCallStack = ({
  toolCalls,
  toolConfig,
  className,
}: ToolCallStackProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const variant = useChatSurfaceVariant();

  // Auto-collapse when new active tool calls arrive
  const activeCount = toolCalls.filter((tc) => tc.streaming).length;
  const prevActiveCountRef = React.useRef(activeCount);
  React.useEffect(() => {
    if (activeCount > prevActiveCountRef.current) {
      setIsExpanded(false);
    }
    prevActiveCountRef.current = activeCount;
  }, [activeCount]);

  if (toolCalls.length === 0) {
    return null;
  }

  const latest = toolCalls[toolCalls.length - 1];
  const hiddenCount = toolCalls.length - 1;
  const collapsible = toolCalls.length > 1;
  const {Icon, isDone, label} = getToolCallDisplay(latest, toolConfig, variant);

  if (!collapsible) {
    return (
      <div className={cn('flex flex-col', className)}>
        <ToolCallRow Icon={Icon} isDone={isDone} label={label} />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <AnimatePresence initial={false}>
        {isExpanded &&
          toolCalls.slice(0, -1).map((toolCall) => {
            const display = getToolCallDisplay(toolCall, toolConfig, variant);
            return (
              <motion.div
                key={toolCall.toolCallId}
                initial={{opacity: 0, height: 0, marginBottom: 0}}
                animate={{opacity: 1, height: 'auto', marginBottom: 6}}
                exit={{opacity: 0, height: 0, marginBottom: 0}}
                transition={{duration: 0.15, ease: 'easeOut'}}
                className="overflow-hidden"
              >
                <ToolCallRow
                  Icon={display.Icon}
                  isDone={display.isDone}
                  label={display.label}
                />
              </motion.div>
            );
          })}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className={cn(
          TOOL_CALL_ROW,
          'text-left transition-colors hover:text-foreground',
        )}
      >
        <ToolCallIcon Icon={Icon} isDone={isDone} />
        <span className={cn(!isDone && 'text-shimmer')}>{label}</span>
        {hiddenCount > 0 && (
          <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground/60">
            +{hiddenCount}
            <Icons.chevronDown
              className={cn(
                'size-3 transition-transform',
                isExpanded && 'rotate-180',
              )}
            />
          </span>
        )}
      </button>
    </div>
  );
};

function ToolCallRow({
  Icon,
  isDone,
  label,
}: {
  Icon: IconComponent;
  isDone: boolean;
  label: string;
}) {
  return (
    <div className={TOOL_CALL_ROW}>
      <ToolCallIcon Icon={Icon} isDone={isDone} />
      <span className={cn(!isDone && 'text-shimmer')}>{label}</span>
    </div>
  );
}

function ToolCallIcon({Icon, isDone}: {Icon: IconComponent; isDone: boolean}) {
  return (
    <Icon className={cn('mt-0.5 size-4 shrink-0', !isDone && 'animate-spin')} />
  );
}
